class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector(".f-section-header");
  }

  connectedCallback() {
    this.classes = {
      active: "f-header__mega-active",
      headerScheme: this.dataset.headerColorScheme,
      dropdownScheme: this.dataset.dropdownColorScheme,
    };

    this.grandLinks = this.querySelectorAll(".f-site-nav__sub-item--has-child");
    this.grandLinks &&
      this.grandLinks.forEach((item) => {
        this.handleGrandLinksPosition(item);
      });
  }

  handleMegaItemActive(dropdown) {
    if (!dropdown) return;
    const rect = dropdown.getBoundingClientRect();
    this.style.setProperty(
      "--f-dropdown-height",
      Math.ceil(rect.height) + "px"
    );

    this.classList.remove(this.classes.headerScheme);
    this.classList.add(this.classes.active, this.classes.dropdownScheme);
    if (dropdown.hasAttribute("data-chillbee-mega-menu")) {
      this.classList.add("chillbee-mega-open");
      this.classList.remove("chillbee-panel-open");
    } else if (dropdown.classList.contains("f-site-nav__dropdown--chillbee")) {
      this.classList.add("chillbee-panel-open");
      this.classList.remove("chillbee-mega-open");
    }
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleMegaItemDeactive() {
    this.classList.remove(
      this.classes.active,
      this.classes.dropdownScheme,
      "chillbee-mega-open",
      "chillbee-panel-open"
    );
    this.classList.add(this.classes.headerScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleGrandLinksPosition(target) {
    const dropdownLV3 = target.querySelector(".f-site-nav__dropdown");
    if (dropdownLV3) {
      const rect = dropdownLV3.getBoundingClientRect();
      dropdownLV3.classList.remove("f-site-nav__dropdown-reversed");
      if (
        (!FoxThemeSettings.isRTL &&
          document.documentElement.clientWidth < rect.x + rect.width + 10) ||
        (FoxThemeSettings.isRTL && rect.x < 10)
      ) {
        dropdownLV3.classList.add("f-site-nav__dropdown-reversed");
      }
    }
  }
}
customElements.define("basic-header", BasicHeader, { extends: "header" });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.stickyClasses = {
      pinned: "header-pinned",
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.headerSection.classList.add("header-sticky");
    this.stickyType = this.dataset.stickyType;
    this.currentScrollTop = 0;

    if (this.isAlwaysSticky) {
      document.body.classList.add("header-sticky-always", this.stickyClasses.pinned);
    }

    this.headerBounds = this.headerSection
      .querySelector(".header")
      .getBoundingClientRect();

    this._updateHeaderHeight();
    this.onScrollHandler = this._onScroll.bind(this);
    this.onResizeHandler = this._updateHeaderHeight.bind(this);
    this._onScroll();
    window.addEventListener("scroll", this.onScrollHandler, false);
    window.addEventListener("resize", this.onResizeHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler);
    window.removeEventListener("resize", this.onResizeHandler);
  }

  _updateHeaderHeight() {
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.offsetHeight + "px"
    );
  }

  get isAlwaysSticky() {
    return this.stickyType === "always";
  }

  _onScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    const headerBoundsTop = this.offsetTop + this.headerBounds.height;

    const headerBoundsBottom =
      this.headerBounds.top + this.headerBounds.height + 100;

    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add("header-scrolled");

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.stickyClasses.pinned);
        } else {
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom
          ) {
            document.body.classList.add(this.stickyClasses.pinned);
          } else {
            document.body.classList.remove(this.stickyClasses.pinned);
          }
        }
      } else {
        headerSection.classList.remove("header-scrolled");
        if (!this.isAlwaysSticky) {
          document.body.classList.remove(this.stickyClasses.pinned);
        }
      }

      this.currentScrollTop = scrollTop;
    });
  }

  _closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());

    this.siteNav = this.siteNav || this.header.querySelector("site-nav");
    this.siteNav && this.siteNav.closeAllDropdowns();
  }
}
customElements.define("sticky-header", StickyHeader, { extends: "header" });

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };
    this.header.timeoutEnter = null;
    this.header.timeoutLeave = null;
  }

  onToggle(evt) {
    const { target } = evt;
    const li = target.closest(".f-site-nav__item");
    const isOpen = this.mainDetailsToggle.open;
    const isMega = li.classList.contains("f-site-nav__item--chillbee-mega");

    if (!isOpen) {
      clearTimeout(this.header.timeoutEnter);
      li.classList.remove(this.classes.itemActive);
      this._setExpanded(li, false);
      this.header.timeoutLeave = setTimeout(() => {
        this.header.handleMegaItemDeactive();
      }, 160);
    } else {
      if (isMega) {
        clearTimeout(this.header.timeoutLeave);
        const dropdown = li.querySelector(".f-site-nav__dropdown");
        this.header.handleMegaItemActive(dropdown);
        this.header.timeoutEnter = setTimeout(() => {
          li.classList.add(this.classes.itemActive);
          this._setExpanded(li, true);
        }, 160);
      } else {
        li.classList.add(this.classes.itemActive);
        this._setExpanded(li, true);
      }
    }
  }

  _setExpanded(li, expanded) {
    const link = li && li.querySelector(".f-site-nav__link");
    if (link) link.setAttribute("aria-expanded", expanded ? "true" : "false");
  }
}
customElements.define("header-menu", HeaderMenu);

class SiteNav extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };

    this.dropdownItems = this.querySelectorAll(
      ".f-site-nav__item--chillbee-has-dropdown, .f-site-nav__item--mega"
    );
    this.timeoutEnter = null;
    this.timeoutLeave = null;
    this.isHover =
      this.header &&
      this.header.classList.contains("show-dropdown-menu-on-hover");

    this._onKeydown = this._handleKeydown.bind(this);
    document.addEventListener("keydown", this._onKeydown);

    if (this.isHover && this.dropdownItems.length) {
      this.dropdownItems.forEach((item, index) => {
        item.addEventListener("mouseenter", (evt) =>
          this.onMenuItemEnter(evt, index)
        );
        item.addEventListener("mouseleave", (evt) =>
          this.onMenuItemLeave(evt, index)
        );
        item.addEventListener("focusin", (evt) =>
          this.onMenuItemEnter(evt, index)
        );
        item.addEventListener("focusout", (evt) =>
          this.onMenuItemFocusOut(evt, index)
        );
      });
    }
  }

  _handleKeydown(evt) {
    if (evt.key === "Escape") {
      this.closeAllDropdowns();
    }
  }

  onMenuItemFocusOut(evt) {
    const { target, relatedTarget } = evt;
    if (relatedTarget && target.contains(relatedTarget)) {
      return;
    }
    this.onMenuItemLeave(evt);
  }

  onMenuItemEnter(evt) {
    clearTimeout(this.timeoutLeave);

    const { target } = evt;
    const isMega = target.classList.contains("f-site-nav__item--chillbee-mega");
    const dropdown = target.querySelector(".f-site-nav__dropdown");

    if (!target.classList.contains(this.classes.itemActive)) {
      this.dropdownItems.forEach((item) => {
        item.classList.remove(this.classes.itemActive);
        this._setExpanded(item, false);
      });
    }

    if (dropdown) {
      this.header.handleMegaItemActive(dropdown);

      this.timeoutEnter = setTimeout(() => {
        target.classList.add(this.classes.itemActive);
        this._setExpanded(target, true);
      }, isMega ? 0 : 80);
    }
  }

  onMenuItemLeave(evt) {
    const { target } = evt;

    clearTimeout(this.timeoutEnter);

    this.timeoutLeave = setTimeout(() => {
      target.classList.remove(this.classes.itemActive);
      this._setExpanded(target, false);
      this.header.handleMegaItemDeactive();
    }, 120);
  }

  _setExpanded(li, expanded) {
    const link = li && li.querySelector(".f-site-nav__link");
    if (link) link.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  closeAllDropdowns() {
    clearTimeout(this.timeoutEnter);
    clearTimeout(this.timeoutLeave);
    this.dropdownItems.forEach((item) => {
      item.classList.remove(this.classes.itemActive);
      this._setExpanded(item, false);
    });
    this.header.handleMegaItemDeactive();
  }

  closeMegaDropdowns() {
    this.closeAllDropdowns();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this._onKeydown);
    if (!this.dropdownItems) return;
    this.dropdownItems.forEach((item, index) => {
      item.removeEventListener("mouseenter", (evt) =>
        this.onMenuItemEnter(evt, index)
      );
      item.removeEventListener("mouseleave", (evt) =>
        this.onMenuItemLeave(evt, index)
      );
      item.removeEventListener("focusin", (evt) =>
        this.onMenuItemEnter(evt, index)
      );
      item.removeEventListener("focusout", (evt) =>
        this.onMenuItemFocusOut(evt, index)
      );
    });
  }
}
customElements.define("site-nav", SiteNav);
